import { getRuntimeConfig } from "../config/runtime";
import { displayNameForIdentity } from "../utils/privacy";
import { clearSelectedAccountId } from "../account/selection";

export interface CognitoTokenPayload {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  "cognito:username"?: string;
  "cognito:groups"?: string[];
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

export interface StoredAuthTokens {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresAt?: number;
}

const TOKEN_STORAGE_KEY = "surelinc.cmmc.auth.tokens";
const PKCE_VERIFIER_KEY = "surelinc.cmmc.auth.pkceVerifier";
const AUTH_STATE_KEY = "surelinc.cmmc.auth.state";
const RETURN_TO_KEY = "surelinc.cmmc.auth.returnTo";
const AUTH_CALLBACK_PATH = "/auth/callback";
const OAUTH_SCOPE = "openid email profile";

function base64UrlDecode(value: string): string {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
  return decodeURIComponent(
    atob(padded)
      .split("")
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join("")
  );
}

function base64UrlEncode(bytes: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(bytes));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function createRandomString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function createPkceChallenge(verifier: string): Promise<string> {
  const encoded = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  return base64UrlEncode(digest);
}

export function decodeJwt(token: string): CognitoTokenPayload {
  const [, payload] = token.split(".");
  if (!payload) {
    throw new Error("Invalid JWT token format");
  }
  return JSON.parse(base64UrlDecode(payload)) as CognitoTokenPayload;
}

export function getStoredTokens(): StoredAuthTokens | null {
  const raw = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as StoredAuthTokens;
  } catch {
    clearStoredTokens();
    return null;
  }
}

export function setStoredTokens(tokens: StoredAuthTokens): void {
  sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
}

export function clearStoredTokens(): void {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

export function clearAuthSession(): void {
  clearStoredTokens();
  clearSelectedAccountId();
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(AUTH_STATE_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
}

export function getAccessToken(): string | null {
  const tokens = getStoredTokens();
  return tokens?.accessToken ?? null;
}

export function getIdToken(): string | null {
  const tokens = getStoredTokens();
  return tokens?.idToken ?? null;
}

export function getApiAuthorizationToken(): string | null {
  // API Gateway Cognito authorizers for this app expect the ID token.
  // The ID token also carries user attributes such as custom:clientId that
  // the backend uses for client scoping after authorization succeeds.
  return getIdToken() ?? getAccessToken();
}

export function getAuthorizationToken(): string | null {
  return getIdToken() ?? getAccessToken();
}

export function getIdTokenPayload(): CognitoTokenPayload | null {
  const tokens = getStoredTokens();
  if (!tokens?.idToken) return null;
  return decodeJwt(tokens.idToken);
}

export function getAccessTokenPayload(): CognitoTokenPayload | null {
  const tokens = getStoredTokens();
  if (!tokens?.accessToken) return null;
  return decodeJwt(tokens.accessToken);
}

export function isTokenExpired(token: string, skewSeconds = 60): boolean {
  const payload = decodeJwt(token);
  if (!payload.exp) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSeconds + skewSeconds;
}

export function isAuthenticated(): boolean {
  const token = getAccessToken();
  if (!token) return false;

  try {
    return !isTokenExpired(token);
  } catch {
    return false;
  }
}

export function getUserGroups(): string[] {
  const payload = getIdTokenPayload() ?? getAccessTokenPayload();
  const groups = payload?.["cognito:groups"];
  return Array.isArray(groups) ? groups : [];
}

export function getSignedInUserEmail(): string | null {
  const payload = getIdTokenPayload() ?? getAccessTokenPayload();
  return typeof payload?.email === "string" ? payload.email : null;
}

export function getSignedInUserDisplayName(fallback = "Signed-in user"): string {
  const payload = getIdTokenPayload() ?? getAccessTokenPayload();
  return displayNameForIdentity(payload as Record<string, unknown> | null, fallback);
}

export function getSignedInClientId(): string | null {
  const payload = getIdTokenPayload() ?? getAccessTokenPayload();
  const clientId = payload?.["custom:clientId"] ?? payload?.clientId;
  return typeof clientId === "string" && clientId.trim() ? clientId.trim() : null;
}

export function hasGroup(groupName: string): boolean {
  return getUserGroups().includes(groupName);
}

export function isPlatformAdmin(): boolean {
  return hasGroup(getRuntimeConfig().adminGroupName);
}

export function isClientAdmin(): boolean {
  return hasGroup(getRuntimeConfig().clientAdminGroupName);
}

export function isClientReadonly(): boolean {
  return hasGroup(getRuntimeConfig().clientReadonlyGroupName);
}

export function canDownloadClientArtifacts(): boolean {
  return isPlatformAdmin() || isClientAdmin();
}

export function getFriendlyUserRole(): string {
  if (isPlatformAdmin()) return "SURELINC admin";
  if (isClientAdmin()) return "Client admin";
  if (isClientReadonly()) return "Read-only access";

  const groups = getUserGroups();
  return groups.length > 0 ? "Limited access" : "Signed-in user";
}

function ensureAuthCallbackPath(value?: string | null): string {
  const fallbackOrigin = window.location.origin;

  try {
    const baseValue = value && value.trim() ? value.trim() : fallbackOrigin;
    const url = new URL(baseValue, fallbackOrigin);

    if (url.pathname.replace(/\/+$/, "") !== AUTH_CALLBACK_PATH) {
      url.pathname = AUTH_CALLBACK_PATH;
    }

    url.search = "";
    url.hash = "";

    return url.toString();
  } catch {
    return `${fallbackOrigin}${AUTH_CALLBACK_PATH}`;
  }
}

export function getAuthCallbackUrl(): string {
  return ensureAuthCallbackPath(window.location.origin);
}

export function buildCognitoHostedUiLoginUrl(redirectUri?: string, state?: string, codeChallenge?: string): string {
  const config = getRuntimeConfig();

  if (!config.cognitoDomain) {
    throw new Error("cognitoDomain or VITE_COGNITO_DOMAIN is required to use hosted sign in.");
  }

  const url = new URL(`https://${config.cognitoDomain}/login`);
  url.searchParams.set("client_id", config.cognitoUserPoolClientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", OAUTH_SCOPE);
  url.searchParams.set("redirect_uri", ensureAuthCallbackPath(redirectUri));
  if (state) url.searchParams.set("state", state);
  if (codeChallenge) {
    url.searchParams.set("code_challenge", codeChallenge);
    url.searchParams.set("code_challenge_method", "S256");
  }
  return url.toString();
}

export function buildCognitoHostedUiLogoutUrl(logoutUri: string): string {
  const config = getRuntimeConfig();

  if (!config.cognitoDomain) {
    throw new Error("cognitoDomain or VITE_COGNITO_DOMAIN is required to use hosted sign in.");
  }

  const url = new URL(`https://${config.cognitoDomain}/logout`);
  url.searchParams.set("client_id", config.cognitoUserPoolClientId);
  url.searchParams.set("logout_uri", logoutUri);
  return url.toString();
}

export async function startHostedUiLogin(returnTo = "/"): Promise<void> {
  const verifier = createRandomString(64);
  const challenge = await createPkceChallenge(verifier);
  const state = createRandomString(24);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(AUTH_STATE_KEY, state);
  sessionStorage.setItem(RETURN_TO_KEY, returnTo);

  window.location.assign(buildCognitoHostedUiLoginUrl(getAuthCallbackUrl(), state, challenge));
}

export interface AuthCallbackResult {
  returnTo: string;
}

export async function handleHostedUiCallback(callbackUrl: string = window.location.href): Promise<AuthCallbackResult> {
  const config = getRuntimeConfig();
  const url = new URL(callbackUrl);
  const error = url.searchParams.get("error");
  const errorDescription = url.searchParams.get("error_description");

  if (error) {
    throw new Error(errorDescription || error);
  }

  const code = url.searchParams.get("code");
  const returnedState = url.searchParams.get("state");
  const expectedState = sessionStorage.getItem(AUTH_STATE_KEY);
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);

  if (!code) {
    throw new Error("Missing authorization code in sign-in callback.");
  }
  if (!returnedState || !expectedState || returnedState !== expectedState) {
    throw new Error("Invalid sign-in callback state. Please sign in again.");
  }
  if (!verifier) {
    throw new Error("Missing PKCE verifier. Please sign in again.");
  }
  if (!config.cognitoDomain) {
    throw new Error("cognitoDomain or VITE_COGNITO_DOMAIN is required to exchange the authorization code.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.cognitoUserPoolClientId,
    code,
    redirect_uri: getAuthCallbackUrl(),
    code_verifier: verifier,
  });

  const response = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });

  const parsed = (await response.json()) as {
    access_token?: string;
    id_token?: string;
    refresh_token?: string;
    expires_in?: number;
    error?: string;
    error_description?: string;
  };

  if (!response.ok || !parsed.access_token) {
    throw new Error(parsed.error_description || parsed.error || "Unable to complete sign in.");
  }

  setStoredTokens({
    accessToken: parsed.access_token,
    idToken: parsed.id_token,
    refreshToken: parsed.refresh_token,
    expiresAt: parsed.expires_in ? Date.now() + parsed.expires_in * 1000 : undefined,
  });

  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(AUTH_STATE_KEY);
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || "/";
  sessionStorage.removeItem(RETURN_TO_KEY);

  return { returnTo };
}

export function signOutTo(logoutUri: string): void {
  clearAuthSession();
  window.location.assign(buildCognitoHostedUiLogoutUrl(logoutUri));
}

export function signOut(): void {
  signOutTo(window.location.origin);
}
